import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Card, Grid,
  IconButton, Tooltip, LinearProgress, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  CloudUpload, Close, AttachFile, Delete, DeleteForever,
  FolderOpen, Description, CheckCircle, ErrorOutline,
  CloudDone, Refresh as RefreshIcon, Warning,
  Visibility, CloudDownload, Assignment, EventBusy
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import workOrderDetailService from '../../services/workOrderDetailService';
import { useDocumentCategories } from '../../constants/documentTypes';
import { getExpirationStatus } from '../../utils/documentExpiration';
import { MAX_BRANCH_DOCUMENT_UPLOAD_MB as MAX_FILE_SIZE_MB } from '../../constants/uploadLimits';

// Mismo criterio que AccessControlService (backend): Admin, Coordinador de Operaciones y
// Supervisor de Calidad ven todos los entes; cualquier otro rol (Ejecutivo de Operaciones) solo
// los entes en los que esté asignado dentro de ESA orden de trabajo.
const UNRESTRICTED_ROLES = ['ADMINISTRADOR', 'COORDINADOR_OPERACIONES', 'SUPERVISOR_CALIDAD'];
const MATRIZ_VALUE = 'MATRIZ';

/**
 * Diálogo único de subida de documentos de cumplimiento, con selector de ente
 * (matriz + cada sucursal). Reutilizable desde tres puntos de entrada distintos
 * — pasa el que corresponda, solo uno a la vez:
 *   - workOrder: desde Programas.jsx (contexto de una orden de trabajo)
 *   - company:   desde CatalogoEmpresas.jsx (contexto de una empresa/matriz)
 *   - branch:    desde GestionSucursales.jsx (contexto de una sucursal específica,
 *                que además preselecciona esa sucursal en el selector de ente)
 */
export default function UploadDocumentosDialog({ open, onClose, workOrder, company, branch, onSuccess, onError }) {
  const companyId = workOrder?.companyId ?? company?.id ?? branch?.companyId;
  const companyName = workOrder?.companyName ?? company?.businessName ?? branch?.companyName ?? 'Empresa';
  const initialEntity = branch ? String(branch.id) : MATRIZ_VALUE;

  const [branches, setBranches] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(initialEntity);
  const [files, setFiles] = useState({});
  const [expirationDates, setExpirationDates] = useState({});
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  // null = sin restricción calculada todavía (o no aplica, ej. contexto company/branch); una vez
  // calculado en contexto workOrder para un rol restringido, es el Set de keys de ente
  // (MATRIZ_VALUE o String(branchId)) en las que el usuario actual está asignado en ESA orden.
  const [assignedEntityKeys, setAssignedEntityKeys] = useState(null);

  useEffect(() => {
    api.get('/users/me').then(res => setCurrentUser(res.data)).catch(() => setCurrentUser(null));
  }, []);

  const currentRole = (currentUser?.role?.name || currentUser?.role || '').toString().toUpperCase();
  const isAdmin = currentRole === 'ADMIN' || currentRole === 'ADMINISTRADOR';

  // Restringe el selector de ente a lo que el usuario actual tiene asignado en esta orden de
  // trabajo -- antes mostraba TODOS los entes de la empresa sin importar el rol ni la asignación.
  useEffect(() => {
    if (!open || !workOrder?.id || !currentUser) {
      setAssignedEntityKeys(null);
      return;
    }
    if (UNRESTRICTED_ROLES.includes(currentRole)) {
      setAssignedEntityKeys(null);
      return;
    }
    workOrderDetailService.getDetailsByWorkOrder(workOrder.id)
      .then((details) => {
        const keys = new Set(
          (details || [])
            .filter((d) => d.assignedUserId === currentUser.id)
            .map((d) => (d.branchId != null ? String(d.branchId) : MATRIZ_VALUE))
        );
        setAssignedEntityKeys(keys);
      })
      .catch(() => setAssignedEntityKeys(new Set()));
  }, [open, workOrder?.id, currentUser, currentRole]);

  const isImage = (filename) => /\.(jpe?g|png|gif|webp|bmp)$/i.test(filename || '');
  const isPDF = (filename) => /\.pdf$/i.test(filename || '');

  // expirationDate llega como "YYYY-MM-DD" (LocalDate, sin hora) -- `new Date(str)` lo
  // interpreta como medianoche UTC y en huso horario de México (UTC-6) muestra un día antes.
  const formatExpirationDate = (dateString) => {
    if (!dateString) return '—';
    const [y, m, d] = dateString.substring(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-MX');
  };

  const isMatriz = selectedEntity === MATRIZ_VALUE;
  const branchId = isMatriz ? null : Number(selectedEntity);
  const scope = isMatriz ? 'matriz' : 'sucursal';
  const visibleBranches = assignedEntityKeys
    ? branches.filter((b) => assignedEntityKeys.has(String(b.id)))
    : branches;
  const showMatrizOption = !assignedEntityKeys || assignedEntityKeys.has(MATRIZ_VALUE);

  // Si el ente preseleccionado (o el último elegido) queda fuera de lo que el usuario tiene
  // asignado, cae al primero que sí puede ver -- evita quedarse en un ente oculto por el filtro.
  useEffect(() => {
    if (!assignedEntityKeys) return;
    const stillValid = isMatriz ? showMatrizOption : assignedEntityKeys.has(selectedEntity);
    if (stillValid) return;
    if (showMatrizOption) {
      setSelectedEntity(MATRIZ_VALUE);
    } else if (visibleBranches.length > 0) {
      setSelectedEntity(String(visibleBranches[0].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedEntityKeys, branches]);
  const { categories: CATEGORIAS_DOCUMENTOS } = useDocumentCategories();
  // El catálogo de documentos requeridos es el mismo para cualquier ente (matriz o
  // sucursal) -- cada uno es un inmueble distinto y necesita sus propios legales,
  // dictámenes, permisos, etc.
  const docsForEntity = CATEGORIAS_DOCUMENTOS.flatMap((cat) => cat.docs);

  // Cargar sucursales de la empresa al abrir el diálogo
  useEffect(() => {
    if (open && companyId) {
      setSelectedEntity(initialEntity);
      setFiles({});
      setExpirationDates({});
      setResult(null);
      api.get(`/company-branches/company/${companyId}`)
        .then((res) => setBranches((res.data || []).filter((b) => b.isActive !== false)))
        .catch(() => setBranches([]));
    }
    if (!open) {
      setUploadedDocs({});
      setBranches([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId]);

  const loadUploadedDocs = useCallback(async () => {
    if (!open || !companyId) return;
    try {
      setLoadingDocs(true);
      const url = isMatriz
        ? `/branch-documents/company/${companyId}/matriz`
        : `/branch-documents/branch/${branchId}`;
      const res = await api.get(url);
      const map = {};
      (res.data || []).forEach((doc) => { map[doc.documentType] = doc; });
      setUploadedDocs(map);
    } catch (e) {
      console.warn('No se pudieron cargar documentos existentes:', e.message);
      setUploadedDocs({});
    } finally {
      setLoadingDocs(false);
    }
  }, [open, companyId, isMatriz, branchId]);

  useEffect(() => { loadUploadedDocs(); }, [loadUploadedDocs]);

  // Solo ADMIN puede borrar (coincide con @PreAuthorize("hasRole('ADMIN')") del backend).
  const handleDeleteDocument = async (doc) => {
    if (!isAdmin) return;
    if (!window.confirm(`¿Eliminar "${doc.documentName}"? Esta acción no se puede deshacer desde aquí.`)) return;
    setDeletingKey(doc.documentType);
    try {
      await api.delete(`/branch-documents/${doc.id}`);
      await loadUploadedDocs();
    } catch (e) {
      if (onError) onError(e.response?.data?.message || 'No se pudo eliminar el documento.');
    } finally {
      setDeletingKey(null);
    }
  };

  const handlePreviewFile = async (doc) => {
    setPreviewDoc(doc);
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const response = await api.get('/dropbox/preview-file', {
        params: { path: doc.filePath },
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
      setPreviewUrl(blobUrl);
    } catch (err) {
      console.error('Error cargando preview:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadFile = async (doc) => {
    try {
      const response = await api.get('/dropbox/download-file', {
        params: { path: doc.filePath },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.documentName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando archivo:', err);
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null);
    setPreviewUrl(null);
  };

  const attachedCount = Object.values(files).filter(Boolean).length;

  const handleFileSelect = (key, event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      if (onError) onError(`El archivo "${file.name}" supera el límite de ${MAX_FILE_SIZE_MB}MB.`);
      event.target.value = '';
      return;
    }

    setFiles(prev => ({ ...prev, [key]: file }));
    event.target.value = '';
  };

  const handleRemove = (key) => {
    setFiles(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setExpirationDates(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleExpirationChange = (key, value) => {
    setExpirationDates(prev => ({ ...prev, [key]: value }));
  };

  const handleUpload = async () => {
    const pending = Object.entries(files).filter(([, f]) => f);
    if (!pending.length || (isMatriz ? !companyId : !branchId)) return;

    setUploading(true);
    setResult(null);
    setProgress(0);
    let ok = 0, fail = 0;
    const errors = [];
    const succeededKeys = [];

    for (let i = 0; i < pending.length; i++) {
      const [key, file] = pending[i];
      try {
        const fd = new FormData();
        fd.append('file', file);
        if (isMatriz) fd.append('companyId', companyId); else fd.append('branchId', branchId);
        fd.append('documentType', key);
        fd.append('scope', scope);
        // Opcional -- si el usuario no la captura, se sube igual sin fecha de vencimiento.
        if (expirationDates[key]) fd.append('expirationDate', expirationDates[key]);
        await api.post('/branch-documents/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        });
        ok++;
        succeededKeys.push(key);
      } catch (e) {
        fail++;
        // El rechazo por tamaño de Spring (413, antes de llegar al controller) responde
        // {error: "..."} en vez de {message: "..."} -- sin el fallback a data?.error, ese caso
        // caía al mensaje genérico de axios ("Request failed with status code 413").
        errors.push(`${key}: ${e.response?.data?.message || e.response?.data?.error || e.message}`);
      }
      setProgress(Math.round(((i + 1) / pending.length) * 100));
    }

    setUploading(false);

    // Solo se limpia la selección local de los que sí se subieron — los que fallaron
    // deben seguir mostrando su archivo seleccionado, no desaparecer como si hubiera éxito.
    if (succeededKeys.length > 0) {
      setFiles(prev => {
        const next = { ...prev };
        succeededKeys.forEach(k => { delete next[k]; });
        return next;
      });
      setExpirationDates(prev => {
        const next = { ...prev };
        succeededKeys.forEach(k => { delete next[k]; });
        return next;
      });
    }

    if (fail === 0) {
      setResult({ success: true, message: `${ok} documento(s) subido(s) exitosamente` });
      await loadUploadedDocs();
      if (onSuccess) onSuccess({ uploadedCount: ok });
      setTimeout(() => handleClose(), 1800);
    } else {
      setResult({ success: ok > 0, message: `${ok} subido(s), ${fail} fallaron: ${errors.join(', ')}` });
      await loadUploadedDocs();
      if (onError) onError(`${fail} documento(s) no se pudieron subir.`);
    }
  };

  const handleClose = () => {
    if (uploading) return;
    setFiles({});
    setExpirationDates({});
    setProgress(0);
    setResult(null);
    onClose();
  };

  const entityLabel = isMatriz ? 'Matriz' : (branches.find(b => b.id === branchId)?.branchName || 'Sucursal');

  // Resumen calculado con lo ya cargado (catálogo de tipos + documentos persistidos) --
  // no depende del endpoint /branch-documents/branch/{id}/stats (no cubre matriz ni vencimientos).
  const summary = docsForEntity.reduce((acc, doc) => {
    const persisted = uploadedDocs[doc.key];
    if (persisted) {
      acc.uploaded++;
      const { isExpired, isNearExpiry } = getExpirationStatus(persisted.expirationDate);
      if (isExpired) acc.expired++;
      else if (isNearExpiry) acc.nearExpiry++;
    }
    return acc;
  }, { uploaded: 0, expired: 0, nearExpiry: 0 });
  summary.total = docsForEntity.length;
  summary.missing = summary.total - summary.uploaded;

  const summaryTiles = [
    { label: 'Requeridos', value: summary.total, color: '#1565c0', bgcolor: '#e3f2fd', Icon: Assignment },
    { label: 'Subidos', value: summary.uploaded, color: '#2e7d32', bgcolor: '#e8f5e9', Icon: CheckCircle },
    { label: 'Faltantes', value: summary.missing, color: '#616161', bgcolor: '#f5f5f5', Icon: Description },
    { label: 'Por vencer', value: summary.nearExpiry, color: '#e65100', bgcolor: '#fff3e0', Icon: EventBusy },
    { label: 'Vencidos', value: summary.expired, color: '#c62828', bgcolor: '#ffebee', Icon: EventBusy },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 20px 40px rgba(0,0,0,0.18)', maxHeight: '92vh' } }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
        color: 'white',
        py: 2.5, px: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FolderOpen sx={{ fontSize: 30 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Documentos de Cumplimiento
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {workOrder
                  ? `${workOrder.orderNumber || `OT-${workOrder.id}`} · ${companyName}`
                  : branch
                    ? `${companyName} · ${branch.branchName}`
                    : companyName}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              label={`${attachedCount} / ${docsForEntity.length}`}
              size="small"
              sx={{
                bgcolor: attachedCount > 0 ? 'rgba(76,175,80,0.85)' : 'rgba(255,255,255,0.18)',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.8rem',
              }}
            />
            <IconButton onClick={handleClose} sx={{ color: 'white' }} disabled={uploading} size="small">
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      {/* ── Selector de ente ── */}
      <Box sx={{ px: 3, py: 1.5, bgcolor: '#e8eeff', borderBottom: '1px solid #c7d3f5', display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Ente</InputLabel>
          <Select
            label="Ente"
            value={selectedEntity}
            onChange={(e) => { setSelectedEntity(e.target.value); setFiles({}); setExpirationDates({}); }}
            disabled={uploading}
          >
            {showMatrizOption && (
              <MenuItem value={MATRIZ_VALUE}>Matriz — {companyName}</MenuItem>
            )}
            {visibleBranches.map((b) => (
              <MenuItem key={b.id} value={String(b.id)}>{b.branchName}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          {loadingDocs && <CircularProgress size={13} sx={{ color: '#1565c0' }} />}
          {!loadingDocs && (
            <Chip
              label={`${Object.keys(uploadedDocs).length} subido${Object.keys(uploadedDocs).length !== 1 ? 's' : ''}`}
              size="small"
              color={Object.keys(uploadedDocs).length > 0 ? 'success' : 'default'}
              variant="outlined"
              icon={<CloudDone sx={{ fontSize: '13px !important' }} />}
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
          <Tooltip title="Recargar documentos">
            <span>
              <IconButton size="small" onClick={loadUploadedDocs} disabled={loadingDocs || uploading}>
                <RefreshIcon sx={{ fontSize: 14, color: '#1565c0' }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Nota Informativa ── */}
      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <Alert
          severity="info"
          icon={<Warning />}
          sx={{
            bgcolor: '#e3f2fd',
            borderLeft: '4px solid #2196f3',
            '& .MuiAlert-icon': { color: '#1565c0' }
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Documentos de <strong>{entityLabel}</strong>. Cada sucursal y la matriz tienen su propia lista —
            cambia el ente arriba para subir o revisar los documentos de otro.
          </Typography>
        </Alert>
      </Box>

      {/* ── Resumen ── */}
      {!loadingDocs && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Grid container spacing={1.5}>
            {summaryTiles.map(({ label, value, color, bgcolor, Icon }) => (
              <Grid size={{ xs: 12/summaryTiles.length }} key={label}>
                <Card sx={{ bgcolor, borderLeft: `3px solid ${color}`, boxShadow: 'none', border: '1px solid #eee' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1 }}>
                    <Icon sx={{ fontSize: 20, color }} />
                    <Box>
                      <Typography sx={{ fontWeight: 700, color, lineHeight: 1.1, fontSize: '1.1rem' }}>{value}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{label}</Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <DialogContent sx={{ p: 0 }}>
        <TableContainer sx={{ maxHeight: 430 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 36, bgcolor: '#1a237e', color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>#</TableCell>
                <TableCell sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>Descripción</TableCell>
                <TableCell sx={{ width: 180, bgcolor: '#1a237e', color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>Archivo a subir</TableCell>
                <TableCell sx={{ width: 130, bgcolor: '#1a237e', color: 'white', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center' }}>Estado en Dropbox</TableCell>
                <TableCell sx={{ width: 130, bgcolor: '#1a237e', color: 'white', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center' }}>Fecha subida</TableCell>
                <TableCell sx={{ width: 66, bgcolor: '#1a237e', color: 'white', fontWeight: 700, fontSize: '0.75rem', textAlign: 'center' }}>Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docsForEntity.map((doc, idx) => {
                const attached = !!files[doc.key];
                const persisted = uploadedDocs[doc.key];
                const isReplacing = attached && !!persisted;

                let rowBg = idx % 2 === 0 ? '#fafafa' : 'white';
                if (isReplacing) rowBg = '#fffbeb';
                else if (attached) rowBg = '#f0fff4';
                else if (persisted) rowBg = '#f0f7ff';

                return (
                  <TableRow
                    key={doc.key}
                    sx={{
                      bgcolor: rowBg,
                      '&:hover': { bgcolor: attached ? (isReplacing ? '#fef3c7' : '#e6ffed') : (persisted ? '#e0f0ff' : '#f0f4ff') },
                      transition: 'background 0.15s',
                    }}
                  >
                    <TableCell sx={{ color: '#777', fontWeight: 600, fontSize: '0.75rem' }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.4, color: '#222' }}>
                        {doc.label}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {attached ? (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isReplacing ? '#92400e' : '#2e7d32', fontWeight: 600,
                              display: 'block', overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              maxWidth: 165,
                            }}
                            title={files[doc.key].name}
                          >
                            {files[doc.key].name}
                          </Typography>
                          {isReplacing && (
                            <Typography variant="caption" sx={{ color: '#b45309', fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <Warning sx={{ fontSize: 10 }} /> Reemplazará el existente
                            </Typography>
                          )}
                          <Tooltip title="Fecha de vencimiento del documento (opcional)">
                            <input
                              type="date"
                              value={expirationDates[doc.key] || ''}
                              onChange={(e) => handleExpirationChange(doc.key, e.target.value)}
                              disabled={uploading}
                              style={{
                                marginTop: 4, fontSize: '0.68rem', border: '1px solid #cbd5e1',
                                borderRadius: 4, padding: '2px 4px', width: '100%', boxSizing: 'border-box',
                                color: '#475569', outline: 'none',
                              }}
                            />
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#bbb', fontStyle: 'italic' }}>
                          Sin seleccionar
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {persisted ? (
                        <Chip
                          label="Subido"
                          size="small"
                          color="primary"
                          variant="filled"
                          icon={<CheckCircle sx={{ fontSize: '12px !important' }} />}
                          sx={{ fontSize: '0.66rem', height: 20, bgcolor: '#1565c0' }}
                        />
                      ) : (
                        <Chip
                          label="Pendiente"
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{ fontSize: '0.66rem', height: 20, color: '#94a3b8', borderColor: '#cbd5e1' }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {persisted ? (
                        <Box>
                          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#1565c0', fontWeight: 600, display: 'block', lineHeight: 1.3 }}>
                            {persisted.createdAt ? new Date(persisted.createdAt).toLocaleDateString('es-MX') : '—'}
                          </Typography>
                          {persisted.expirationDate && (() => {
                            const { isExpired, isNearExpiry } = getExpirationStatus(persisted.expirationDate);
                            const label = isExpired
                              ? 'Vencido'
                              : isNearExpiry
                                ? 'Por vencer'
                                : `Vigente: ${formatExpirationDate(persisted.expirationDate)}`;
                            return (
                              <Chip
                                label={label}
                                size="small"
                                color={isExpired ? 'error' : isNearExpiry ? 'warning' : 'default'}
                                variant={isExpired || isNearExpiry ? 'filled' : 'outlined'}
                                sx={{ fontSize: '0.6rem', height: 16, mt: 0.3, color: isExpired || isNearExpiry ? undefined : '#94a3b8', borderColor: '#cbd5e1' }}
                              />
                            );
                          })()}
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.3, mt: 0.3 }}>
                            {persisted.filePath && (isImage(persisted.documentName) || isPDF(persisted.documentName)) && (
                              <Tooltip title="Vista previa">
                                <IconButton
                                  size="small"
                                  onClick={() => handlePreviewFile(persisted)}
                                  sx={{ p: 0.3, color: '#7c3aed' }}
                                >
                                  <Visibility sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {persisted.filePath && (
                              <Tooltip title="Descargar">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadFile(persisted)}
                                  sx={{ p: 0.3, color: '#2e7d32' }}
                                >
                                  <CloudDownload sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#bbb', fontSize: '0.68rem' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {attached ? (
                        <Tooltip title="Quitar archivo seleccionado">
                          <span>
                            <IconButton size="small" color="error" onClick={() => handleRemove(doc.key)} disabled={uploading}>
                              <Delete sx={{ fontSize: 15 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
                            style={{ display: 'none' }}
                            id={`docup-${doc.key}`}
                            onChange={(e) => handleFileSelect(doc.key, e)}
                            disabled={uploading}
                          />
                          <label htmlFor={`docup-${doc.key}`}>
                            <Tooltip title={persisted ? 'Subir nueva versión' : 'Adjuntar archivo'}>
                              <span>
                                <IconButton size="small" color={persisted ? 'warning' : 'primary'} component="span" disabled={uploading}>
                                  <AttachFile sx={{ fontSize: 15 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </label>
                          {persisted && isAdmin && (
                            <Tooltip title="Eliminar documento">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteDocument(persisted)}
                                  disabled={uploading || deletingKey === doc.key}
                                  sx={{ color: '#c62828' }}
                                >
                                  {deletingKey === doc.key
                                    ? <CircularProgress size={13} color="inherit" />
                                    : <DeleteForever sx={{ fontSize: 15 }} />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Preview Modal ── */}
        {previewDoc && (
          <Dialog
            open={!!previewDoc}
            onClose={closePreview}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
          >
            <DialogTitle sx={{ background: 'linear-gradient(135deg, #1565c0, #0d47a1)', color: 'white', py: 1.5, px: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Visibility sx={{ fontSize: 18 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                  {previewDoc.documentName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  size="small"
                  startIcon={<CloudDownload sx={{ fontSize: 14 }} />}
                  onClick={() => handleDownloadFile(previewDoc)}
                  sx={{ color: 'white', textTransform: 'none', fontSize: '0.78rem', borderColor: 'rgba(255,255,255,0.5)', border: '1px solid' }}
                >
                  Descargar
                </Button>
                <IconButton size="small" onClick={closePreview} sx={{ color: 'white' }}>
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0, bgcolor: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
              {previewLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
                  <CircularProgress sx={{ color: 'white' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>Cargando vista previa...</Typography>
                </Box>
              ) : previewUrl ? (
                isImage(previewDoc.documentName) ? (
                  <img
                    src={previewUrl}
                    alt={previewDoc.documentName}
                    style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', display: 'block' }}
                  />
                ) : isPDF(previewDoc.documentName) ? (
                  <iframe
                    src={previewUrl}
                    title={previewDoc.documentName}
                    style={{ width: '100%', height: '75vh', border: 'none' }}
                  />
                ) : null
              ) : (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', py: 6 }}>No se pudo cargar la vista previa</Typography>
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* ── Progress & result ── */}
        {(uploading || result) && (
          <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
            {uploading && (
              <>
                <Typography variant="body2" sx={{ mb: 1, color: '#1565c0', fontWeight: 500 }}>
                  Subiendo {attachedCount} archivo{attachedCount !== 1 ? 's' : ''}… {progress}%
                </Typography>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
              </>
            )}
            {result && !uploading && (
              <Alert
                severity={result.success ? 'success' : 'error'}
                icon={result.success ? <CheckCircle /> : <ErrorOutline />}
                sx={{ py: 0.5 }}
              >
                {result.message}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{
        px: 3, py: 2,
        borderTop: '1px solid #e0e0e0',
        bgcolor: '#f5f7ff',
        justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Description sx={{ fontSize: 15, color: '#888' }} />
          <Typography variant="caption" color="text.secondary">
            PDF, JPG, PNG, Word, Excel · Máx. {MAX_FILE_SIZE_MB}MB por archivo
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            size="small"
            disabled={uploading}
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            size="small"
            startIcon={!uploading && <CloudUpload sx={{ fontSize: 17 }} />}
            disabled={attachedCount === 0 || uploading}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              px: 3,
              '&:hover': { background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)' },
              '&:disabled': { background: '#e0e0e0', color: '#aaa' },
            }}
          >
            {uploading
              ? `Subiendo… ${progress}%`
              : `Subir (${attachedCount} archivo${attachedCount !== 1 ? 's' : ''})`
            }
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
